import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { clearLoginAttemptInfo, consumeCode, getLoginAttemptInfo } from "supertokens-web-js/recipe/passwordless";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

enum PageState {
  Verifying,
  DifferentDevice,
  Error,
}

let hasConsumed = false;

export function MagicLinkCallbackPage(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [state, setState] = useState(PageState.Verifying);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [deviceCode, setDeviceCode] = useState<string | undefined>();

  useEffect(() => {
    if (hasConsumed) {
      return;
    }

    hasConsumed = true;

    const queryParams = new URLSearchParams(window.location.search);
    const linkCode = queryParams.get("linkCode");

    void (async () => {
      if (!linkCode) {
        setState(PageState.Error);

        return;
      }

      // If there's no active login attempt on this device, the link was opened
      // on a different device than where sign-in was initiated. Show the code
      // so the user can enter it on the original device.
      const loginAttemptInfo = await getLoginAttemptInfo();
      if (!loginAttemptInfo) {
        const userInputCode = queryParams.get("userInputCode");
        setDeviceCode(userInputCode ?? undefined);
        setState(PageState.DifferentDevice);

        return;
      }

      // The SDK reads linkCode from window.location.hash, not query params
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${linkCode}`);

      try {
        const response = await consumeCode();

        if (response.status === "OK") {
          if (!response.createdNewRecipeUser) {
            // Existing user just logging in - same as a regular login, skip straight to the app
            void navigate("/");

            return;
          }

          const code = queryParams.get("code");
          const type = queryParams.get("type");

          const newQueryParams = new URLSearchParams();
          if (code) {
            newQueryParams.set("code", code);
          }

          if (type) {
            newQueryParams.set("type", type);
          }

          void navigate(`${routes.registration.main()}?${newQueryParams.toString()}`);
        } else if (response.status === "SIGN_IN_UP_NOT_ALLOWED") {
          setErrorMessage(response.reason);
          setState(PageState.Error);
        } else {
          // RESTART_FLOW_ERROR: link expired or already used
          await clearLoginAttemptInfo();
          setState(PageState.Error);
        }
      } catch (err: any) {
        if (err.isSuperTokensGeneralError === true) {
          setErrorMessage(err.message);
        }

        setState(PageState.Error);
      }
    })();
  }, [navigate]);

  let header = " ";
  if (state === PageState.Error) {
    header = t("page.magic-link.callback.error.title");
  } else if (state === PageState.DifferentDevice) {
    header = t("page.magic-link.callback.different-device.title");
  }

  return (
    <AuthenticationPage header={header}>
      {state === PageState.Verifying && <FullSizeLoader />}
      {state === PageState.DifferentDevice && (
        <Stack gap="lg">
          <p>{t("page.magic-link.callback.different-device.description")}</p>
          {deviceCode && (
            <Stack gap="xs">
              <p className="text-caption text-text-secondary">
                {t("page.magic-link.callback.different-device.code-label")}
              </p>
              <p className="text-heading-2 tracking-widest">{deviceCode}</p>
            </Stack>
          )}
        </Stack>
      )}
      {state === PageState.Error && (
        <Stack gap="xl">
          <p>{errorMessage || t("page.magic-link.callback.error.description")}</p>
          <Button type="link" href={routes.authentication.login()} fullWidth>
            {t("page.magic-link.callback.error.retry")}
          </Button>
        </Stack>
      )}
    </AuthenticationPage>
  );
}
